import { Button } from "@nodus/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard, safeRedirect, takeRedirect } from "../../../app/auth-card";
import { useAuth } from "../../../auth/auth-context";

// External return route (ui-spec §2.8): success, denied, expired or state mismatch, and error, never a dead end.
export const Route = createFileRoute("/auth/callback")({
  validateSearch: (s: Record<string, unknown>) => ({
    code: typeof s.code === "string" ? s.code : undefined,
    error: typeof s.error === "string" ? s.error : undefined,
    next: typeof s.next === "string" ? s.next : undefined,
  }),
  component: AuthCallback,
});

function AuthCallback() {
  const { code, error, next } = Route.useSearch();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = React.useState<"working" | "denied" | "expired" | "error">(error ? (error === "access_denied" ? "denied" : "error") : "working");
  React.useEffect(() => {
    if (!code || error) {
      if (!code && !error) setState("expired");
      return;
    }
    auth
      .exchangeCode(code)
      .then(() => navigate({ href: next ? safeRedirect(next) : takeRedirect(), replace: true }))
      .catch(() => setState("expired"));
  }, [code, error, next, auth, navigate]);
  if (state === "working") return <AuthCard title="Signing you in…" />;
  const again = <Button onClick={() => navigate({ to: "/sign-in" })}>Start again</Button>;
  if (state === "denied") return <AuthCard title="Sign-in was cancelled" description="Nothing changed. You can try again.">{again}</AuthCard>;
  if (state === "expired") return <AuthCard title="This link has expired" description="Sign-in links work once and expire quickly.">{again}</AuthCard>;
  return <AuthCard title="Sign-in failed" description={error}>{again}</AuthCard>;
}
